// End-to-end encryption helpers built on the Web Crypto API.
//
// Model: the browser generates a random AES-256-GCM key, encrypts the
// plaintext secret, and sends only { ciphertext, nonce } to the server.
// The raw key is placed in the URL fragment, which browsers never
// transmit in HTTP requests, so proxies and the backend never see the key.
// The recipient's browser reads the key from the fragment and decrypts locally.
//
// Requires a secure context (HTTPS or localhost). Web Crypto is built into
// all modern browsers; no third-party crypto library is used.

const KEY_LENGTH_BYTES = 32; // AES-256
const IV_LENGTH_BYTES = 12; // 96-bit IV recommended for GCM

// --- base64url helpers (no padding) ---

export function bytesToBase64Url(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(str) {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4;
  const b64 = pad ? padded + '='.repeat(4 - pad) : padded;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// --- key generation / transport ---

export async function generateAesKey() {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable so we can put the raw key in the URL fragment
    ['encrypt', 'decrypt']
  );
}

export async function exportKeyToFragment(key) {
  const raw = await crypto.subtle.exportKey('raw', key);
  return bytesToBase64Url(new Uint8Array(raw));
}

export async function importKeyFromFragment(fragmentStr) {
  const raw = base64UrlToBytes(fragmentStr);
  if (raw.length !== KEY_LENGTH_BYTES) {
    throw new Error('Invalid key length in URL fragment');
  }
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt'
  ]);
}

// --- encrypt / decrypt ---

function encodeText(plaintext) {
  return new TextEncoder().encode(plaintext);
}

function decodeText(bytes) {
  return new TextDecoder().decode(bytes);
}

export async function encryptSecret(plaintext, key) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encodeText(plaintext)
  );
  return {
    ciphertext: bytesToBase64Url(new Uint8Array(ciphertextBuf)),
    nonce: bytesToBase64Url(iv)
  };
}

export async function decryptSecret(ciphertextB64, nonceB64, key) {
  const ciphertext = base64UrlToBytes(ciphertextB64);
  const iv = base64UrlToBytes(nonceB64);
  if (iv.length !== IV_LENGTH_BYTES) {
    throw new Error('Invalid nonce length');
  }
  const plaintextBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
  return decodeText(new Uint8Array(plaintextBuf));
}

// --- fragment URL helpers ---

// Read the decryption key from window.location.hash (#k=...).
// Returns null when no key is present.
export function readKeyFromLocation() {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#k=')) {
    return null;
  }
  const value = hash.slice(3);
  return value || null;
}

// Strip the #k=... fragment from the address bar so it does not leak via
// copy-paste of the bare URL, referrer, or screenshots.
export function clearKeyFromLocation() {
  if (window.location.hash && window.location.hash.startsWith('#k=')) {
    const url = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, url);
  }
}

// Validate that a string looks like a base64url AES-256 key (32 bytes -> 43 chars).
export function isValidFragmentKey(str) {
  if (typeof str !== 'string' || !str) return false;
  if (!/^[A-Za-z0-9_-]+$/.test(str)) return false;
  try {
    return base64UrlToBytes(str).length === KEY_LENGTH_BYTES;
  } catch {
    return false;
  }
}
